// ============================================================
//  WrongAnswersScreen.jsx — قائمة الأسئلة الخاطئة
//  تعرض كل الأسئلة التي أخطأ فيها المستخدم مع الإجابة الصحيحة
// ============================================================
import { LogoIcon } from './Logo';

import React, { useState, useEffect } from 'react';
import { getWrongAnswers } from './supabaseClient';
import { useLanguage } from './LanguageContext';

// ── ألوان المواد ──────────────────────────────────────────
const SUBJECT_COLOR = {
  'كمي':       '#3b82f6',
  'لفظي':      '#7c6df8',
  'رياضيات':   '#10b981',
  'فيزياء':    '#06b6d4',
  'كيمياء':    '#8b5cf6',
  'أحياء':     '#22c55e',
};

const getSubjectColor = (subject) =>
  SUBJECT_COLOR[subject] || 'var(--text-secondary)';

// ── بطاقة سؤال واحد ──────────────────────────────────────
function QuestionCard({ item }) {
  const [expanded, setExpanded] = useState(false);
  const q            = item.questions;
  if (!q) return null;

  const options      = Array.isArray(q.options) ? q.options : [];
  const correctIdx   = Number(q.correct_key);      // 0-3
  const chosenIdx    = Number(item.chosen_index);   // 0-3
  const subjectColor = getSubjectColor(q.subject);

  return (
    <div style={{
      background: 'var(--bg-card)',
      border: '1px solid #1e2a42',
      borderRadius: 16,
      overflow: 'hidden',
      marginBottom: 10,
    }}>
      {/* رأس البطاقة */}
      <div
        onClick={() => setExpanded(p => !p)}
        style={{
          padding: '14px 16px',
          cursor: 'pointer',
          display: 'flex',
          gap: 10,
          alignItems: 'flex-start',
        }}
      >
        {/* أيقونة الخطأ */}
        <div style={{
          minWidth: 28, height: 28,
          background: 'rgba(239,68,68,0.12)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, marginTop: 1,
        }}>❌</div>

        {/* نص السؤال */}
        <div style={{ flex: 1 }}>
          <div style={{
            color: 'var(--text)', fontSize: 14, fontWeight: 600,
            lineHeight: 1.6, textAlign: 'right',
            display: '-webkit-box',
            WebkitLineClamp: expanded ? 'unset' : 2,
            WebkitBoxOrient: 'vertical',
            overflow: expanded ? 'visible' : 'hidden',
          }}>
            {q.question_ar}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
            <span style={{
              background: subjectColor + '18',
              border: `1px solid ${subjectColor}44`,
              color: subjectColor,
              fontSize: 11, fontWeight: 700,
              padding: '2px 8px', borderRadius: 6,
            }}>
              {q.subject || 'عام'}
            </span>
            <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
              {expanded ? '▲ إخفاء' : '▼ عرض الإجابات'}
            </span>
          </div>
        </div>
      </div>

      {/* الخيارات — تظهر عند التوسيع */}
      {expanded && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid #1e2a42' }}>

          {/* صورة السؤال إن وجدت */}
          {q.image_url && (
            <img
              src={q.image_url}
              alt="سؤال"
              style={{
                maxWidth: '100%', borderRadius: 10,
                margin: '12px 0', border: '1px solid #334155',
              }}
            />
          )}

          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {options.map((opt, i) => {
              const isCorrect = i === correctIdx;
              const isChosen  = i === chosenIdx;
              const isWrong   = isChosen && !isCorrect;

              let bg      = '#0d1117';
              let border  = 'var(--border)';
              let color   = 'var(--text-secondary)';
              let badge   = null;

              if (isCorrect) {
                bg     = 'rgba(34,197,94,0.08)';
                border = 'rgba(34,197,94,0.4)';
                color  = '#86efac';
                badge  = <span style={{
                  background: '#14532d', color: '#86efac',
                  fontSize: 10, fontWeight: 900,
                  padding: '1px 7px', borderRadius: 5,
                  marginRight: 6,
                }}>✓ صحيح</span>;
              } else if (isWrong) {
                bg     = 'rgba(239,68,68,0.08)';
                border = 'rgba(239,68,68,0.35)';
                color  = '#fca5a5';
                badge  = <span style={{
                  background: '#450a0a', color: '#fca5a5',
                  fontSize: 10, fontWeight: 900,
                  padding: '1px 7px', borderRadius: 5,
                  marginRight: 6,
                }}>✗ اخترته</span>;
              }

              return (
                <div key={i} style={{
                  background: bg,
                  border: `1px solid ${border}`,
                  borderRadius: 10,
                  padding: '10px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  textAlign: 'right',
                }}>
                  <span style={{
                    minWidth: 24, height: 24,
                    background: isCorrect ? 'rgba(34,197,94,0.15)' : isWrong ? 'rgba(239,68,68,0.15)' : 'var(--border)',
                    borderRadius: 6,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: color, fontSize: 12, fontWeight: 700,
                  }}>
                    {String.fromCharCode(65 + i)}
                  </span>
                  <span style={{ color, fontSize: 13, flex: 1 }}>{opt}</span>
                  {badge}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── الشاشة الرئيسية ───────────────────────────────────────
export default function WrongAnswersScreen({ user, onBack }) {
  const { t, isRTL } = useLanguage();
  const [answers,  setAnswers]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState('الكل');

  useEffect(() => {
    if (!user?.id) return;
    getWrongAnswers(user.id).then(({ data }) => {
      setAnswers(data || []);
      setLoading(false);
    });
  }, [user]);

  // مواد الفلتر
  const subjects = ['الكل', ...new Set(
    answers.map(a => a.questions?.subject).filter(Boolean)
  )];

  const filtered = filter === 'الكل'
    ? answers
    : answers.filter(a => a.questions?.subject === filter);

  // ── شاشة التحميل ──
  const pageStyle = { ...styles.page, direction: isRTL ? 'rtl' : 'ltr' };

  if (loading) return (
    <div style={pageStyle}>
      <div style={{ color: '#7c6df8', fontSize: 16, marginTop: 80 }}>
        ⏳ جاري تحميل أسئلتك...
      </div>
    </div>
  );

  return (
    <div style={pageStyle}>

      {/* Header */}
      <div style={{
        background: 'rgba(13,17,23,0.95)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid #1e2a42',
        padding: '16px 20px',
        display: 'flex', alignItems: 'center', gap: 12,
        position: 'sticky', top: 0, zIndex: 10,
        width: '100%', boxSizing: 'border-box',
      }}>
        <button onClick={onBack} style={{
          background: 'none', border: 'none',
          color: 'var(--text-secondary)', fontSize: 22,
          cursor: 'pointer', padding: 0, lineHeight: 1,
        }}>←</button>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <LogoIcon size={28} />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ color: 'var(--text)', fontWeight: 900, fontSize: 17 }}>
            📋 {t('wrong_title')}
          </div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 1 }}>
            {answers.length} سؤال فريد
          </div>
        </div>
        {/* عداد المواد */}
        <div style={{
          background: 'rgba(239,68,68,0.1)',
          border: '1px solid rgba(239,68,68,0.25)',
          borderRadius: 10, padding: '4px 12px',
        }}>
          <span style={{ color: '#fca5a5', fontWeight: 900, fontSize: 18 }}>
            {filtered.length}
          </span>
        </div>
      </div>

      <div style={{ padding: '16px', maxWidth: 540, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>

        {/* فلتر المواد */}
        {subjects.length > 1 && (
          <div style={{
            display: 'flex', gap: 8, overflowX: 'auto',
            paddingBottom: 4, marginBottom: 16,
            scrollbarWidth: 'none',
          }}>
            {subjects.map(sub => {
              const active = filter === sub;
              const color  = sub === 'الكل' ? '#7c6df8' : getSubjectColor(sub);
              return (
                <button
                  key={sub}
                  onClick={() => setFilter(sub)}
                  style={{
                    flexShrink: 0,
                    background: active ? color + '22' : 'transparent',
                    border: `1.5px solid ${active ? color : 'var(--border)'}`,
                    borderRadius: 10, padding: '6px 14px',
                    color: active ? color : 'var(--text-secondary)',
                    fontSize: 13, fontWeight: active ? 700 : 400,
                    cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
                    transition: 'all 0.15s',
                  }}
                >
                  {sub}
                  {sub !== 'الكل' && (
                    <span style={{ marginRight: 4, color: active ? color : 'var(--text-muted)', fontSize: 11 }}>
                      ({answers.filter(a => a.questions?.subject === sub).length})
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* قائمة الأسئلة */}
        {filtered.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '60px 20px',
            background: 'var(--bg-card)', borderRadius: 20,
            border: '1px solid #1e2a42',
          }}>
            <div style={{ fontSize: 56, marginBottom: 12 }}>🎉</div>
            <div style={{ color: '#86efac', fontWeight: 900, fontSize: 18 }}>
              ما فيه أخطاء!
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 8 }}>
              {filter === 'الكل'
                ? 'أجب على أسئلة أكثر لتظهر هنا أخطاؤك'
                : `ما في أخطاء في مادة ${filter}`}
            </div>
          </div>
        ) : (
          <>
            {/* تلميح */}
            <div style={{
              background: 'rgba(124,109,248,0.08)',
              border: '1px solid rgba(124,109,248,0.2)',
              borderRadius: 12, padding: '10px 14px',
              marginBottom: 14,
              color: '#a78bfa', fontSize: 13,
            }}>
              💡 اضغط على أي سؤال لعرض الإجابة الصحيحة
            </div>

            {filtered.map((item, i) => (
              <QuestionCard key={item.id || i} item={item} />
            ))}

            {/* نهاية القائمة */}
            <div style={{
              textAlign: 'center', padding: '20px',
              color: 'var(--border)', fontSize: 13,
            }}>
              — نهاية القائمة —
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── الأنماط — direction تُحدَّد ديناميكياً في المكوّن
const styles = {
  page: {
    minHeight: '100vh',
    background: 'var(--bg-page)',
    fontFamily: 'Tajawal,sans-serif',
    direction: 'rtl',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
};
